import { DEFAULT_ITEMS_PER_PAGE_LIST } from '@/models/constants';
import { TransactionDateKeyList, TransactionListItem } from '@/models/transaction/transaction-list.model';
import { defineStore, storeToRefs } from 'pinia';
import { useTransactionListFilterStore } from './transaction-list-filter.store';
import { clearObj, formatDatesInPayload } from '@marfa/bo-utils';
import { api } from '@/services/http.service';
import useTokenPagination from '@/hooks/use-token-pagination.hook';
import { showErrorNotification } from '@/utils/show-notification.util';

type TransactionListStore = {
  isTransactionListLoading: boolean;
  transactionList: TransactionListItem[];
  limit: number;
  sort: string;
}

export const useTransactionListStore = defineStore({
  id: 'TransactionListStore',
  state: (): TransactionListStore => ({
    isTransactionListLoading: false,
    transactionList: [],
    limit: DEFAULT_ITEMS_PER_PAGE_LIST[0],
    sort: '',
  }),
  getters: {
    transactionListWithFormattedDates(state: TransactionListStore): TransactionListItem[] {
      return state.transactionList.map((transactionItem: TransactionListItem) => formatDatesInPayload({
        payload: transactionItem,
        isUnixToString: true,
        isIncludeTime: true,
      }, [
        TransactionDateKeyList.CREATED_AT,
        TransactionDateKeyList.UPDATED_AT,
      ]));
    },
  },
  actions: {
    setTransactionListLoading(isLoading: boolean): void {
      this.isTransactionListLoading = isLoading;
    },
    setLimit(limit: number): void {
      this.limit = limit;
    },
    setSort(sortOrder: string): void {
      this.sort = sortOrder;
    },
    getTransactionList(wlSlug: string): Promise<TransactionListItem[] | void> {
      this.setTransactionListLoading(true);

      const { submittedFilterFormData } = storeToRefs(useTransactionListFilterStore());
      const {
        prevPageToken,
        nextPageToken,
        processResponseHeaders,
      } = useTokenPagination();

      const requestPayload = {
        limit: this.limit,
        prevPageToken: prevPageToken.value,
        nextPageToken: nextPageToken.value,
        filterList: clearObj(submittedFilterFormData.value),
        sort: this.sort,
      };

      return api.transactions.getTransactionListRequest.perform(wlSlug, requestPayload)
        .then(({ data, headers }) => {
          this.transactionList = data;

          processResponseHeaders(headers);
        })
        .catch((error) => {
          showErrorNotification(error);
        })
        .finally(() => {
          this.setTransactionListLoading(false);
        });
    },
  },
});
